import React, {useEffect, useRef, useState} from "react";
import styled from "styled-components";
import currentTheme from "../../../react-themes/theme";
import {t, translateCollection, useEnsureTranslate, useLocale} from "../../../shared-services/translate";
import {useSelector, useSessionActions} from "../../../shared-services/redux-tiny";
import useModalQueue from "./useModalQueue";
import apiEndpoints from "../../shared/apiEndpoints";
import useWindowTools from "../../../react-hooks/useWindowTools";
import axios from "axios";
import {trackEvent} from "../../../services/tracking-helper";
import useClickOutside from "../../shared/useClickOutside";

const theme = currentTheme();
const imagePath = "/resources/farmy/images/components/modals";
const translationKey = "modals.new_user_promo";
const contextEndpoint = `${apiEndpoints.frontend}/session_context/apply_policy.json`;
const policy = "new_user_email_promo";
const launchDelay = 2500;
const allowedPaths = ["home", "catalog", "pdp", "search_results"];

const NewUserPromoModal = (props) => {
  const modalName = "NewUserPromoModal";
  useEnsureTranslate();
  const {setSessionContext} = useSessionActions();
  const locale = useLocale();
  const [showModal, setShowModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [emailValue, setEmailValue] = useState("");

  const {currentModal, cookieBannerOpen} = useSelector(state => state.topLayer);
  const sessionContext = useSelector(state => state.session.sessionContext);
  const {pageCode} = useSelector(state => state.router);
  const {prerenderAgent} = useSelector(state => state.session);
  const userPromoModalRef = useRef(false);

  const {pushToQueue, closeModal} = useModalQueue(modalName);

  const {isMobile} = useWindowTools();

  useClickOutside(
    ".new-user-promo-modal",
    () => userPromoModalRef.current && isMobile && onClose(),
    [currentModal?.name]);

  const handleInputChange = (event) => {
    setErrorMessage("");
    setEmailValue(event.target.value);
  };

  const applyPolicy = async(email = null) => {
    const params = {policy, locale};
    if (email) params.email = email;
    try {
      const response = await axios.post(contextEndpoint, params);
      if (params.email) window.Alerts.success(t(`${translationKey}.success_alert`));
      setSessionContext(response?.data || []);
      localStorage.setItem("new_user_email_promo_shown", "true");
    } catch (error) {
      const {data} = error.response;
      const errorMessage = data?.error_message || t(`${translationKey}.error_alert`);
      window.Alerts.error(errorMessage);
      console.error("Error applying policy:", {errorMessage});
    }
  };

  const onClose = () => {
    if (currentModal?.name && (currentModal?.name === modalName)) {
      localStorage.setItem("new_user_email_promo_shown", "true");
      closePromoModal();
    }
  };

  const shouldDisplay = () => {
    if (prerenderAgent) return false;

    const correctPath = allowedPaths.includes(pageCode);
    const correctContext = sessionContext?.includes("new_user_email_promo");
    const updatedInSession = localStorage.getItem("new_user_email_promo_shown") === "true";
    return correctContext && correctPath && !updatedInSession && !cookieBannerOpen;
  };
  const isEmailValid = async(email) => {
    // Regular expression for validating the email
    const emailPattern = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]{2,}(?:\.[a-zA-Z0-9-]+)*$/;

    if (email.length === 0) {
      setErrorMessage(t(`${translationKey}.email_errors.empty`));
      return false;
    } else if (!emailPattern.test(email)) {
      setErrorMessage(t(`${translationKey}.email_errors.format`));
      return false;
    } else {
      try {
        return await window.UserService.validators.emailTaken(email);
      } catch (e) {
        setErrorMessage(t(`${translationKey}.email_errors.already_exists`));
        return false;
      }
    }
  };

  const onSubmit = async() => {
    const email = emailValue.trim();
    try {
      const emailValid = await isEmailValid(email);
      if (emailValid) {
        sessionStorage.setItem("user_email_prefill_signup", email);
        applyPolicy(email).then(response => {
          trackEvent("meet_farmy_popup_form");
          closePromoModal();
        });
      }
    } catch (e) {
      console.error(e);
    }
  };

  const launchModal = () => {
    setShowModal(true);
    pushToQueue();
  };

  const closePromoModal = () => {
    if (!userPromoModalRef?.current) return;
    setShowModal(false);
    closeModal();
  };

  useEffect(() => {
    if (!sessionContext) return;
    let launchTimeout;
    if (shouldDisplay()) {
      launchTimeout = setTimeout(() => {
        launchModal();
      }, launchDelay);
    } else {
      closePromoModal();
    }
    return () => {
      clearTimeout(launchTimeout);
    };
  }, [sessionContext, pageCode, currentModal, cookieBannerOpen]);

  useEffect(() => {
    userPromoModalRef.current = true;
    return () => {
      userPromoModalRef.current = false;
    };
  }, []);

  return (
    <StyledNewUserPromoModalWrapper isMobile={isMobile}>
      <StyledNewUserPromoModal
        className={`locale-${locale} ${showModal ? "show-modal" : ""}`}>
        <img src={imagePath + "/blob_top_left.svg"} alt='blob' className='blob blob-top'/>
        <button className={"btn-close"} onClick={onClose}>
          <img src={"/icons/thin-cross-white.svg"} alt={"close"}/>
        </button>
        <div
          className={`content flex flex-direction-column align-items-center content-language-${locale}`}>
          <img src={imagePath + "/new_user_promo/carrot.svg"} alt='carrot'
            className='carrot z-index-500 margin-bottom-10'/>
          <h1 className={"z-index-500"}>{t(`${translationKey}.title`)}</h1>
          {translateCollection(t(`${translationKey}.body1-data-collection`))?.map((text, index) => {
            return (
              <p key={index} className={`body-content margin-0 ${index === 2 ? "highlight" : ""}`}>{text}</p>
            );
          })}
          <div className='email-field text-align-left'>
            <input className={`text-strong ${errorMessage.length ? "error-color error-border" : ""}`}
              type='email' placeholder={"Email"} value={emailValue}
              onChange={handleInputChange}/>
            <p className='error-message text-strong error-color margin-0'>{errorMessage}</p>
          </div>
          <button
            onClick={onSubmit}
            className={"btn-primary btn-accept z-index-500"}
          >
            {t(`${translationKey}.cta`)}
          </button>
        </div>
        <img src={imagePath + "/blob_bottom_right.svg"} alt='blob' className='blob blob-bottom'/>
      </StyledNewUserPromoModal>
    </StyledNewUserPromoModalWrapper>
  );
};
export default NewUserPromoModal;

const StyledNewUserPromoModalWrapper = styled.div.attrs(props => ({
  className: props?.isMobile ? "full-width full-height flex flex-direction-column align-items-center justify-content-flex-end position-fixed padding-bottom-10" : "position-relative"
}))`
  z-index: 7000;
  pointer-events: none;
`;

const StyledNewUserPromoModal = styled.div.attrs({
  className: `
  new-user-promo-modal
  justify-content-center
  flex-direction-column
  align-items-center
  `
})`
  display: flex;
  background-color: ${theme.colors.primaryColor};
  overflow: hidden;
  padding: 32px 10px 37px 10px;
  border-radius: 25px;
  position: fixed;
  top: 25%;
  left: -400px;
  opacity: 0;
  box-shadow: 0 0 25px -8px rgba(0, 0, 0, 0.6);
  max-width: 370px;
  width: 370px;
  transition: left 500ms, opacity 300ms;
  pointer-events: all;
  
  &.show-modal {
    left: 50px;
    opacity: 1;
    @media (max-width: 768px) {
      left: 10px;
    }
  }

  @media (max-width: 768px) {
    width: 90%;
    top: 10px;
  }
  
  @media (max-width: 500px) {
    padding: 32px 32px 37px 32px;
  }
  
  .content {
    text-align: center;
    width: 100%;
    color: #FFFFFF;
    
    .carrot {
      width: 150px;
      height: auto;
    }
    h1 {
      font-size: 24px;
      color: #FFFFFF;
      width: 100%;
      font-weight: 700;
    }
    p {
      font-size: 17px;
      font-weight: 600;
      &.highlight {
        color: #FFAB35;
        font-size: 24px;
        font-weight: 700;
      }
    }
  }
  .btn-close {
    border: none;
    background: none;
    padding: 0;
    top: 24px;
    right: 29px;
    position: absolute;
    img {
      height: 20px;
      width: auto;
    }       
  }
  .email-field {
    color: #FFFFFF;
    width: 100%;
    max-width: 305px;
    margin-top: 60px;
    .error-color {
      color: #FFAA90;
      &::placeholder {
        color: #FFAA90;
      }
    }
    .error-border {
      border-color: #FFAA90;
    }
    @media (max-height: 600px) {
      margin-top: 30px;
    }
    input {
      border: none;
      border-bottom: 2px solid #FFFFFF;
      font-size: 18px;
      background: none;
      width: 100%;
      padding: 5px 12px;
      color: #FFFFFF;
      &:focus {
        outline: none;
      }
      &::placeholder {
        color: #FFFFFF;
      }
    }
    p.error-message {
      height: 30px;
      font-size: 12px;
      padding: 2px 12px;
    }
  }
  .btn-accept {
    min-height: 59px;
    max-width: 305px;
    width: 100%;
    padding: 10px 20px;
    font-size: 24px;
    font-weight: 700;
    border: none;
    outline: none;
    border-radius: 4px;
    position: relative;
    @media (max-width: 370px) {
      font-size: 20px;
    }
  }
  .blob {
    position: absolute;
    &.blob-top {
      top: 0px;
      left: 0;
      height: auto;
      width: 203px;
    }
    &.blob-bottom {
      width: 147px;
      height: auto;
      bottom: 0;
      right: 0;
    }
  }
  .z-index-500 {
    z-index: 500;
  }
`;
